import { useEffect, useRef, useState } from 'preact/hooks';

import { api, type Measure } from '../api/client';
import { pick, type T } from '../i18n';

export type Value = number | string | string[];

/**
 * A page of measures, one or a group, answered in one go (SPEC §9.4, ADR 0002): native radio groups,
 * checkboxes and a text box, each in a fieldset whose legend is the question. Every measure may be
 * skipped; with nothing answered the button says so, so an empty page is never sent by mistake.
 */
export function PageQuestions({
  title,
  stepId,
  questions,
  locale,
  t,
  onSubmitted,
  initial = {},
  onBack,
}: {
  title: string;
  stepId: string;
  questions: Measure[];
  locale: string;
  t: T;
  onSubmitted: () => void;
  /** Answers to show again after going back to this page. */
  initial?: Record<string, Value>;
  /** Back to the previous page, offered while no follow-up or conversation in between has started. */
  onBack?: () => void;
}) {
  const [answers, setAnswers] = useState<Record<string, Value>>(initial);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => heading.current?.focus(), []);

  const set = (id: string, v: Value | undefined) =>
    setAnswers((a) => {
      const next = { ...a };
      if (v === undefined || (Array.isArray(v) && !v.length) || v === '') delete next[id];
      else next[id] = v;
      return next;
    });

  const empty = !Object.keys(answers).length;
  const submit = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { data } = await api.POST('/api/p/sessions/current/answers', { body: { step: stepId, answers } });
    setBusy(false);
    if (data) onSubmitted();
    else setError(t('error.generic'));
  };

  return (
    <main class={questions.length > 1 ? 'measures is-group' : 'measures'}>
      {/* The survey's title is the page heading, set small; each question is set large (visual draft). */}
      <h1 ref={heading} tabIndex={-1} class="measures-title">
        {title}
      </h1>
      <form class="start-form" onSubmit={submit}>
        {questions.map((q) => (
          <MeasureField key={q.id} q={q} value={answers[q.id]} locale={locale} t={t} onChange={(v) => set(q.id, v)} />
        ))}
        {error && (
          <p class="error" role="alert">
            {error}
          </p>
        )}
        <button type="submit" class={empty ? 'button-secondary' : 'button-primary'} disabled={busy}>
          {t(empty ? 'skipPage' : 'pulseSubmit')}
        </button>
        {onBack && (
          <button type="button" class="link-button" disabled={busy} onClick={onBack}>
            {t('back')}
          </button>
        )}
      </form>
    </main>
  );
}

/**
 * One measure as native controls in a fieldset whose legend is the question: on a page and inside a
 * conversation alike, so a measure looks and reads the same wherever it is asked (ADR 0002). Nothing is
 * chosen until the participant chooses.
 */
export function MeasureField({ q, value, locale, t, onChange }: { q: Measure; value: Value | undefined; locale: string; t: T; onChange: (v: Value | undefined) => void }) {
  return (
    <fieldset class="measure">
      <legend>{pick(q.text, locale)}</legend>
      {q.type === 'scale' && (
        <>
          <div class="scale" style={{ '--steps': q.scale.max - q.scale.min + 1 }}>
            {Array.from({ length: q.scale.max - q.scale.min + 1 }, (_, i) => q.scale.min + i).map((v) => (
              <label class="scale-step" key={v}>
                <input type="radio" name={q.id} value={v} checked={value === v} onChange={() => onChange(v)} />
                <span>
                  {v}
                  {/* The end labels are read with their numbers; sighted users see them below. */}
                  {v === q.scale.min && <span class="visually-hidden"> – {pick(q.scale.minLabel, locale)}</span>}
                  {v === q.scale.max && <span class="visually-hidden"> – {pick(q.scale.maxLabel, locale)}</span>}
                </span>
              </label>
            ))}
          </div>
          <p class="scale-labels" aria-hidden="true">
            <span>{pick(q.scale.minLabel, locale)}</span>
            <span>{pick(q.scale.maxLabel, locale)}</span>
          </p>
        </>
      )}
      {q.type === 'single' && (
        <div class="checks">
          {q.options.map((o) => (
            <label class="check" key={o.value}>
              <input type="radio" name={q.id} value={o.value} checked={value === o.value} onChange={() => onChange(o.value)} />
              {pick(o.labels, locale)}
            </label>
          ))}
        </div>
      )}
      {q.type === 'multi' && (
        <div class="checks">
          {q.options.map((o) => {
            const chosen = (value as string[] | undefined) ?? [];
            return (
              <label class="check" key={o.value}>
                <input
                  type="checkbox"
                  value={o.value}
                  checked={chosen.includes(o.value)}
                  onChange={(e) => onChange((e.target as HTMLInputElement).checked ? [...chosen, o.value] : chosen.filter((x) => x !== o.value))}
                />
                {pick(o.labels, locale)}
              </label>
            );
          })}
        </div>
      )}
      {q.type === 'text' && (
        <>
          <label for={`q-${q.id}`} class="visually-hidden">
            {t('textAnswer')}
          </label>
          <textarea id={`q-${q.id}`} rows={3} maxLength={4000} placeholder={t('textPlaceholder')} value={(value as string | undefined) ?? ''} onInput={(e) => onChange((e.target as HTMLTextAreaElement).value)} />
        </>
      )}
    </fieldset>
  );
}

